import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { login as apiLogin, register as apiRegister, getCurrentUser, logout as apiLogout } from '../api/auth';
import axios from 'axios';

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  loading: true,
  login: () => {},
  register: () => {},
  logout: () => {},
  clearSession: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation(); // 获取当前路径

  // 配置axios拦截器，自动为所有请求添加token
  // useEffect(() => {
  //   const interceptor = axios.interceptors.request.use(
  //     (config) => {
  //       const token = localStorage.getItem('token');
  //       if (token) {
  //         config.headers.Authorization = `Bearer ${token}`;
  //       }
  //       return config;
  //     },
  //     (error) => Promise.reject(error)
  //   );

  //   // 清理拦截器
  //   return () => {
  //     axios.interceptors.request.eject(interceptor);
  //   };
  // }, []);

  // 清除所有会话数据
  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          // 从后端获取当前用户信息
          console.log('尝试获取用户信息, token前10位:', token.substring(0, 10));
          const userData = await getCurrentUser();
          console.log('获取到用户数据结果:', userData ? '成功' : '失败', userData);
          
          if (userData) {
            // 验证用户角色与当前URL路径是否匹配
            const currentPath = location.pathname;
            const isTeacherPath = currentPath.startsWith('/teacher');
            const isStudentPath = currentPath.startsWith('/student');
            
            if ((isTeacherPath && userData.role !== 'teacher') || 
                (isStudentPath && userData.role !== 'student')) {
              console.warn('用户角色与访问路径不匹配，清除会话数据');
              clearSession();
            } else {
              setUser(userData);
            }
          } else {
            clearSession();
          }
        }
      } catch (error) {
        console.error('加载用户数据失败', error);
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [location.pathname]);

  const login = async (username, password) => {
    try {
      setLoading(true);
      // 清除任何可能存在的旧数据
      clearSession();
      
      console.log(`AuthContext: 尝试登录 ${username}`);
      
      // 调用真实的API登录
      const response = await apiLogin(username, password);
      console.log('AuthContext: 登录API响应:', response);
      
      if (response && response.token && response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        
        message.success('登录成功');
        console.log('AuthContext: 登录成功，用户角色:', response.user.role);
        
        // 根据角色导航到不同页面
        if (response.user.role === 'teacher') {
          navigate('/teacher');
        } else {
          navigate('/student');
        }
        
        return true;
      } else {
        console.error('AuthContext: 登录响应数据无效', response);
        throw new Error('登录响应数据无效');
      }
    } catch (error) {
      console.error('AuthContext: 登录失败', error);
      message.error('登录失败: ' + (error.message || '未知错误'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      // 调用后端API进行注册
      const response = await apiRegister(userData);
      
      if (response && response.success) {
        message.success(response.message || '注册成功，请登录');
        navigate('/login');
        return true;
      } else {
        throw new Error(response.message || '注册失败');
      }
    } catch (error) {
      message.error('注册失败: ' + (error.message || '未知错误'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiLogout();
      clearSession();
      message.success('已退出登录');
      navigate('/login');
    } catch (error) {
      console.error('登出失败', error);
      clearSession(); // 即使API调用失败，也清除本地会话
      message.error('登出过程中发生错误');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    clearSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};  
